import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, MapPin, PencilLine } from "lucide-react";
import { cn } from "@/lib/cn";
import { useNpAreas, useNpSettlements, useNpStreets } from "@/hooks/useClients";
import type { OutletAddressPayload } from "@/api/clients";

/**
 * Этап 5 — Nova Poshta address-picker with FIRST-CLASS manual fallback.
 * Flow: область → населений пункт → вулиця (settlement-scoped) → будинок.
 * street_dependency_rule: street search needs a settlement. np_street is partial
 * (~208 live) → «немає вашої вулиці» is NORMAL → manual street entry, save never
 * blocked. Emits the OutletAddressPayload via onChange.
 *
 * addressQualityStatus is shown as a live preview (verified_np / mixed /
 * manual_unverified) but the canonical value is re-derived server-side on read.
 */

export interface AddressValue extends OutletAddressPayload {
  settlementName?: string;
  streetName?: string;
}

const fieldCls = "w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm";
const lblCls = "mb-1 block text-[11px] font-medium text-muted-foreground";

export function AddressPicker({ value, onChange }: { value: AddressValue; onChange: (v: AddressValue) => void }) {
  const [manual, setManual] = useState(value.addressSource === "manual");
  const areasQ = useNpAreas(true);

  const areaRef = value.npAreaRef ?? "";
  const settlementRef = value.npSettlementRef ?? "";

  const patch = (p: Partial<AddressValue>) => onChange({ ...value, ...p });

  // ── manual mode ──
  if (manual) {
    return (
      <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><PencilLine className="h-3.5 w-3.5" />Ручний ввід</span>
          <button type="button" onClick={() => { setManual(false); patch({ addressSource: "np" }); }} className="text-[11.5px] font-semibold text-primary hover:underline">
            ← До довідника НП
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className={lblCls}>Місто / нас. пункт</label>
            <input className={fieldCls} value={value.city ?? ""} onChange={(e) => patch({ addressSource: "manual", npAreaRef: null, npSettlementRef: null, npStreetRef: null, city: e.target.value })} placeholder="напр. Бровари" /></div>
          <div><label className={lblCls}>Будинок</label>
            <input className={fieldCls} value={value.buildingText ?? ""} onChange={(e) => patch({ buildingText: e.target.value })} placeholder="№" /></div>
        </div>
        <div><label className={lblCls}>Вулиця</label>
          <input className={fieldCls} value={value.streetText ?? ""} onChange={(e) => patch({ addressSource: "manual", streetText: e.target.value })} placeholder="напр. вул. Київська" /></div>
        <QualityBadge value={{ ...value, addressSource: "manual" }} />
        <p className="text-[11px] text-muted-foreground">Ручна адреса дозволена — позначиться «потребує перевірки», save не блокується.</p>
      </div>
    );
  }

  // ── NP directory mode ──
  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={lblCls}>Область</label>
          <div className="relative">
            <select
              className={fieldCls}
              value={areaRef}
              onChange={(e) => {
                const a = areasQ.data?.items.find((x) => x.ref === e.target.value);
                patch({ addressSource: "np", npAreaRef: e.target.value || null, npSettlementRef: null, npStreetRef: null, settlementName: undefined, city: undefined, streetName: undefined, streetText: undefined });
                void a;
              }}
            >
              <option value="">{areasQ.isPending ? "Завантаження…" : "— область —"}</option>
              {areasQ.data?.items.map((a) => <option key={a.ref} value={a.ref}>{a.name}</option>)}
            </select>
          </div>
        </div>
        <SettlementField
          areaRef={areaRef}
          value={value}
          onPick={(s) => patch({ addressSource: "np", npSettlementRef: s.ref, settlementName: s.name, city: s.name, npStreetRef: null, streetName: undefined, streetText: undefined })}
        />
      </div>

      <StreetField
        settlementRef={settlementRef}
        value={value}
        onPick={(s) => patch({ npStreetRef: s.ref, streetName: s.name, streetText: s.name })}
        onManualStreet={(t) => patch({ npStreetRef: null, streetName: undefined, streetText: t })}
      />

      <div><label className={lblCls}>Будинок</label>
        <input className={fieldCls} value={value.buildingText ?? ""} onChange={(e) => patch({ buildingText: e.target.value })} placeholder="№" /></div>

      <QualityBadge value={value} />
      <button type="button" onClick={() => { setManual(true); patch({ addressSource: "manual", npAreaRef: null, npSettlementRef: null, npStreetRef: null }); }} className="text-[11.5px] font-semibold text-info-foreground hover:underline">
        ↳ Немає вашої адреси в довіднику? Ввести вручну
      </button>
      <p className="text-[11px] text-muted-foreground">Вулиця залежить від населеного пункту. Довідник НП частковий — ручний ввід дозволено.</p>
    </div>
  );
}

// ── settlement combobox (area-scoped search) ─────────────────────────────────

function SettlementField({ areaRef, value, onPick }: {
  areaRef: string; value: AddressValue; onPick: (s: { ref: string; name: string }) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const q2 = useNpSettlements(areaRef, q, open && (!!areaRef || q.length >= 2));
  useOutsideClose(ref, () => setOpen(false));

  const label = value.settlementName ?? "";
  return (
    <div ref={ref} className="relative">
      <label className={lblCls}>Населений пункт</label>
      <input
        className={fieldCls}
        value={open ? q : label}
        onFocus={() => { setOpen(true); setQ(""); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder={areaRef ? "почніть вводити…" : "оберіть область"}
        disabled={!areaRef && q.length === 0}
      />
      {open && (
        <div className="absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-md border bg-popover p-1 shadow-lg">
          {q2.isFetching ? (
            <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Пошук…</div>
          ) : (q2.data?.items.length ?? 0) === 0 ? (
            <div className="px-2 py-2 text-xs text-muted-foreground">Нічого не знайдено</div>
          ) : q2.data!.items.map((s) => (
            <button key={s.ref} type="button" onClick={() => { onPick(s); setOpen(false); }} className="block w-full rounded px-2 py-1.5 text-left text-[12.5px] hover:bg-muted">
              {s.name} {s.type && <span className="text-muted-foreground">· {s.type}</span>}
              {s.areaName && <span className="text-muted-foreground"> · {s.areaName}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── street combobox (settlement-scoped; manual fallback inline) ──────────────

function StreetField({ settlementRef, value, onPick, onManualStreet }: {
  settlementRef: string; value: AddressValue;
  onPick: (s: { ref: string; name: string }) => void;
  onManualStreet: (t: string) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const q2 = useNpStreets(settlementRef, q, open && !!settlementRef);
  useOutsideClose(ref, () => setOpen(false));

  const hasNpStreet = !!value.npStreetRef;
  const label = value.streetName ?? value.streetText ?? "";

  return (
    <div ref={ref} className="relative">
      <label className={lblCls}>Вулиця {!hasNpStreet && value.streetText && <span className="text-warning-foreground">(вручну)</span>}</label>
      <input
        className={fieldCls}
        value={open ? q : label}
        onFocus={() => { if (settlementRef) { setOpen(true); setQ(""); } }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); onManualStreet(e.target.value); }}
        placeholder={settlementRef ? "вулиця з довідника або вручну" : "спершу оберіть нас. пункт"}
        disabled={!settlementRef}
      />
      {open && settlementRef && (
        <div className="absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-md border bg-popover p-1 shadow-lg">
          {q2.isFetching ? (
            <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Пошук…</div>
          ) : (q2.data?.items.length ?? 0) === 0 ? (
            <div className="px-2 py-2 text-[11.5px] text-muted-foreground">
              Немає у довіднику — введіть вручну (буде «потребує перевірки»).
            </div>
          ) : q2.data!.items.map((s) => (
            <button key={s.ref} type="button" onClick={() => { onPick(s); setOpen(false); }} className="block w-full rounded px-2 py-1.5 text-left text-[12.5px] hover:bg-muted">
              {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── quality preview ──────────────────────────────────────────────────────────

function QualityBadge({ value }: { value: AddressValue }) {
  const status = deriveQuality(value);
  const map: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    verified_np: { label: "Перевірено НП (verified_np)", cls: "bg-success text-success-foreground", icon: <Check className="h-3 w-3" /> },
    mixed: { label: "НП частково · вулиця вручну (mixed)", cls: "bg-warning text-warning-foreground", icon: <MapPin className="h-3 w-3" /> },
    manual_unverified: { label: "Ручна · потребує перевірки", cls: "bg-warning text-warning-foreground", icon: <PencilLine className="h-3 w-3" /> },
    needs_review: { label: "Адресу не вказано", cls: "bg-muted text-muted-foreground", icon: <ChevronDown className="h-3 w-3" /> },
  };
  const m = map[status];
  return <span className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-medium", m.cls)}>{m.icon}{m.label}</span>;
}

export function deriveQuality(v: AddressValue): "verified_np" | "mixed" | "manual_unverified" | "needs_review" {
  if (v.addressSource === "np") {
    if (v.npStreetRef) return "verified_np";
    if (v.npSettlementRef) return "mixed";
    return "needs_review";
  }
  return (v.city || v.streetText) ? "manual_unverified" : "needs_review";
}

function useOutsideClose(ref: React.RefObject<HTMLElement>, onClose: () => void) {
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, onClose]);
}

export const emptyAddress: AddressValue = { addressSource: "np", npAreaRef: null, npSettlementRef: null, npStreetRef: null };
